import { CloudIcon, ArrowDownTrayIcon, ExclamationCircleIcon, PencilIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { MouseEventHandler, memo, useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CircleSpinner } from '../../../../components/Button';
import { ContextMenu, useCloseContextMenu } from '../../../../components/ContextMenu';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { useRename } from '../../../../hooks/modules/renderer/useRename';
import useOpen from '../../../../hooks/useOpen';
import { selectPointCloud } from '../../../../redux/rendererReducer';
import { T, useT } from '../../../../translation/src';
import { PointCloud, ProjectCopyType, UploadStatus, useProjectPointCloudRelaunchMutation } from '../../../../types/graphqlTypes';
import { ToastType, useToastNotifications } from '../../../../hooks/useToastNotifications';
import { PointCloudDeleteModal } from '../../../project/PointCloudDeleteModal';
import { AnnotationsIconsWrapper } from '../Annotations/AnnotationsIconsWrapper';
import { UserContext } from '../../../../contexts/UserContext';
import { useDownload } from '../../../../hooks/useDownload';
import { useProject } from '../../../../hooks/potree/useProject';
import { RendererReadOnlyContext } from '../../../../contexts/RendererReadOnlyContext';

// Closes the menu right away; the relaunch answer can take a while.
const RelaunchMenuItem: React.FC<{ onRelaunch: () => void }> = ({ onRelaunch }) => {
  const closeMenu = useCloseContextMenu();
  const onClick = useCallback(() => {
    closeMenu();
    onRelaunch();
  }, [closeMenu, onRelaunch]);
  return (
    <SelectItemContent icon={ArrowPathIcon} onClick={onClick} className="text-black bg-error-red-500 dark:hover:text-white">
      <T _str="relaunch import" swc />
    </SelectItemContent>
  );
};

interface PointCloudLayerProps {
  pointCloud: Pick<PointCloud, 'id' | 'cloudName' | 'visible' | 'displayName' | 'uploadStatus' | 'availableClasses'>;
  active?: boolean;
  rootActive?: boolean;
  className?: string;
  groupIdentifier?: string;
  refreshSuggested?: boolean;
  groupHidden?: boolean;
  isLastPointCloud?: boolean;
}

const PointCloudLayer_: React.FC2<PointCloudLayerProps> = ({ active, pointCloud, className, rootActive, refreshSuggested, groupIdentifier, groupHidden, isLastPointCloud }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { open: deleteModalOpen, onClose: closeDeleteModal, onOpen: openDeleteModal } = useOpen();
  const { organisationId = '' } = useParams();
  const currentUser = useContext(UserContext);
  const readOnly = useContext(RendererReadOnlyContext);
  const progressText = useT('progress', { swc: true });
  const uploadFailedText = useT('upload failed', { swc: true });

  const { downloadOriginalFile, downloadProgress } = useDownload();

  const onDownloadSourceFile = useCallback(async () => {
    if (!projectId) return;
    downloadOriginalFile(projectId, pointCloud.id);
  }, [downloadOriginalFile, projectId, pointCloud.id]);

  // Superadmin: rebuild just this point cloud. The answer carries its fresh (processing) entry; the old
  // one leaves the cache.
  const { showToast } = useToastNotifications();
  const relaunchFailed = useT('could not relaunch the point cloud', { swc: true });
  const [relaunchPointCloud] = useProjectPointCloudRelaunchMutation();
  const onRelaunch = useCallback(async () => {
    if (!projectId) return;
    try {
      await relaunchPointCloud({ variables: { projectId, pointCloudId: pointCloud.id }, update: (cache) => cache.evict({ id: `PointCloud:${pointCloud.id}` }) });
    } catch (e) {
      showToast({ title: relaunchFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [projectId, pointCloud.id, relaunchPointCloud, relaunchFailed, showToast]);

  const isFlex = useMemo(() => {
    const organisation = currentUser.organisations.find((organisation) => organisation.id === organisationId);
    return organisation?.subscription.isFlex;
  }, [currentUser.organisations, organisationId]);

  // The sources of a soft copy belong to the organisation it was copied from: only a superadmin
  // downloads them (the server refuses everyone else). Cached query — the page already ran it.
  const { data: projectData } = useProject();
  const isSoftCopy = projectData?.projectById.copy?.type === ProjectCopyType.Copy;
  const canDownloadSource = currentUser.isSuperAdmin || (!isFlex && !isSoftCopy);

  const [{ onToggleNameEditable }, { nameEditable, nameInputComponent }] = useRename({
    name: pointCloud.displayName,
    pointCloudIdentifier: pointCloud.id,
  });

  const onClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (pointCloud.uploadStatus !== UploadStatus.Success || refreshSuggested) return;
      event.preventDefault();
      event.stopPropagation();
      dispatch(selectPointCloud({ name: pointCloud.cloudName }));
    },
    [dispatch, pointCloud.cloudName, pointCloud.uploadStatus, refreshSuggested],
  );

  const actions = useMemo(() => {
    if (pointCloud.uploadStatus === UploadStatus.Success)
      if (!refreshSuggested) return <AnnotationsIconsWrapper identifier={pointCloud.id} visible={pointCloud.visible} pointCloud={true} />;
      else
        return (
          <div className="flex items-center justify-center w-6 h-6 ml-auto rounded-sm cursor-pointer text-[#6F7173] hover:text-neon-green-300" onClick={() => window.location.reload()}>
            <ArrowPathIcon className="w-4 h-4" />
          </div>
        );
    if (pointCloud.uploadStatus === UploadStatus.Progress)
      return (
        <div className="flex items-center justify-center w-6 h-6 ml-auto" title={progressText}>
          <CircleSpinner className="w-4 h-4 text-neon-green-300" />
        </div>
      );
    return (
      <div className="flex items-center justify-center w-6 h-6 ml-auto" title={uploadFailedText}>
        <ExclamationCircleIcon className="w-4 h-4 text-red-500" />
      </div>
    );
    // return null
  }, [pointCloud.id, pointCloud.uploadStatus, pointCloud.visible, progressText, refreshSuggested, uploadFailedText]);

  if (nameEditable) return nameInputComponent;

  return (
    <>
      <ContextMenu
        buttonChild={
          <SelectItemContent
            key={pointCloud.id}
            icon={CloudIcon}
            selectStyle="primary"
            idle={!pointCloud.visible || pointCloud.uploadStatus !== UploadStatus.Success}
            onClick={onClick}
            active={rootActive || active}
            activeLevel={rootActive ? 1 : 0}
            className={classNames(!className?.includes('pl-') && 'pl-8', className)}
            onDoubleClick={onToggleNameEditable}
          >
            <div className="pr-1 overflow-hidden truncate">{pointCloud.displayName}</div>
            {actions}
          </SelectItemContent>
        }
        panelClassName="divide-y"
        popoverMenuDisabled={readOnly}
      >
        {pointCloud.uploadStatus === UploadStatus.Success && (
          <div className="flex flex-col">
            <SelectItemContent icon={PencilIcon} onClick={onToggleNameEditable}>
              <T _str="rename" swc />
            </SelectItemContent>
          </div>
        )}
        <div className="flex flex-col">
          {canDownloadSource && (
            downloadProgress !== null ? (
              <div className="flex items-center gap-2 px-2 py-1 text-sm">
                <ArrowDownTrayIcon className="w-4 h-4 shrink-0" />
                {downloadProgress >= 0 ? (
                  <>
                    <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600 rounded overflow-hidden">
                      <div className="h-full bg-neon-green-500 transition-all" style={{ width: `${downloadProgress}%` }} />
                    </div>
                    <span className="text-xs tabular-nums">{downloadProgress}%</span>
                  </>
                ) : (
                  <span className="text-xs tabular-nums">{Math.abs(downloadProgress).toFixed(1)} MB</span>
                )}
              </div>
            ) : (
              <SelectItemContent icon={ArrowDownTrayIcon} onClick={onDownloadSourceFile}>
                <T _str="Download Source" swc />
              </SelectItemContent>
            )
          )}
          {currentUser.isSuperAdmin && pointCloud.uploadStatus !== UploadStatus.Progress && (
            <RelaunchMenuItem onRelaunch={onRelaunch} />
          )}
          <SelectItemContent icon={TrashIcon} onClick={openDeleteModal}>
            <T _str="delete" swc />
          </SelectItemContent>
        </div>
      </ContextMenu>
      <PointCloudDeleteModal open={deleteModalOpen} onClose={closeDeleteModal} pointCloudId={pointCloud.id} isLastPointCloud={isLastPointCloud} />
    </>
  );
};
export const PointCloudLayer = memo(PointCloudLayer_);
